import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import {
    Box, Card, Grid, Typography, Button, Chip,
    Avatar, MenuItem, TextField, InputAdornment,
    Tooltip, CircularProgress, IconButton, Menu, Divider,
    Popover, Paper, Tabs, Tab,
} from '@mui/material';
import { List } from 'react-window';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import http from '../../Api/http';
import { useSelector } from 'react-redux';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import { GetEmployeeAttendance, GetAttendanceEmployeeBefore, PostEmployeeManualAttendance } from '../../Api/Api';
import useFinancialYear from '../../hooks/useFinancialYear';
import { toApiDate } from '../../utils/apiFields';
import { SkeletonRows } from '../uiKit';
import { useCrumbActionsSetter } from '../../utils/crumbActions';
import SearchSelect from '../SearchSelect';
import AttStatCard from './AttStatCard';
import SnackBar from '../SnackBar';

const today = new Date().toISOString().split('T')[0];

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const PRIMARY_DARK = '#6246E0';
const PRIMARY_BORDER = '#C9BEFB';

const SCHOOL_START_HOUR = 9;
const LATE_THRESHOLD_MIN = 15;
const DEFAULT_CHECK_OUT = '17:00';

const ROLE_CONFIG = {
    'Teaching Staff':     { color: '#1D4ED8', bg: '#F3F0FE', border: '#C9BEFB' },
    'Non Teaching Staff': { color: '#0E7490', bg: '#ECFEFF', border: '#A5F3FC' },
    'Supporting Staff':   { color: '#C2410C', bg: '#FFF7ED', border: '#FED7AA' },
};

const STATUS_STYLE = {
    'Present':  { color: '#6246E0', bg: '#F1EEFE', border: '#C9BEFB' },
    'Absent':   { color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA' },
    'Late':     { color: '#B45309', bg: '#E0F2FE', border: '#BAE6FD' },
    'On Leave': { color: '#1D4ED8', bg: '#F3F0FE', border: '#C9BEFB' },
};

const AVATAR_PALETTE = ['#0E7490', '#1D4ED8', '#C2410C', '#6246E0', '#1D4ED8', '#BE185D', '#A16207', '#0F766E'];
const avatarColorFor = (name = '') => {
    const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const STATUS_API_TO_UI = {
    'present': 'Present',
    'absent':  'Absent',
    'late':    'Late',
    'leave':   'On Leave',
};

const STATUS_UI_TO_API = {
    'Present':  'present',
    'Absent':   'absent',
    'Late':     'late',
    'On Leave': 'leave',
};

const STATUS_OPTIONS = ['Present', 'Late', 'Absent', 'On Leave'];

const toHHmm = (date = new Date()) =>
    `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const getCurrentTime = () => toHHmm(new Date());

const parseHHmm = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};

const isLateTime = (checkInHHmm) => {
    const mins = parseHHmm(checkInHHmm);
    if (mins === null) return false;
    return mins > (SCHOOL_START_HOUR * 60 + LATE_THRESHOLD_MIN);
};

const computeWorkingHours = (checkIn, checkOut) => {
    const a = parseHHmm(checkIn);
    const b = parseHHmm(checkOut);
    if (a === null || b === null || b <= a) return null;
    const diff = b - a;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return { hours: h, minutes: m, label: `${h}h ${m}m`, totalMinutes: diff };
};

const formatMinutes = (mins) => {
    if (!Number.isFinite(mins) || mins <= 0) return '0m';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const computeBreakDuration = (out, back) => {
    const a = parseHHmm(out);
    const b = parseHHmm(back);
    if (a === null || b === null || b <= a) return 0;
    return b - a;
};

const computeTotalBreakMinutes = (breaks = []) =>
    breaks.reduce((sum, br) => sum + computeBreakDuration(br.out, br.in), 0);

const BREAK_PRESETS = [
    { label: 'Morning Tea', out: '11:00', in: '11:15', icon: LocalCafeIcon, accent: '#0EA5E9', accentBg: '#E0F2FE', accentBorder: '#BAE6FD' },
    { label: 'Lunch',       out: '13:00', in: '13:30', icon: RestaurantIcon, accent: '#C2410C', accentBg: '#FFF7ED', accentBorder: '#FED7AA' },
    { label: 'Evening Tea', out: '16:00', in: '16:15', icon: LocalCafeIcon, accent: '#A16207', accentBg: '#FEFCE8', accentBorder: '#FEF08A' },
];

const makeBreakId = () => `br_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const StatusPill = memo(function StatusPill({ value, selected, onClick, locked }) {
    const s = STATUS_STYLE[value];
    return (
        <Box
            onClick={locked ? undefined : onClick}
            sx={{
                px: 1.1, py: '3px', borderRadius: '50px',
                cursor: locked ? 'not-allowed' : 'pointer',
                border: `1px solid ${selected ? s.color : '#E5E7EB'}`,
                bgcolor: selected ? s.bg : '#fff',
                opacity: locked && !selected ? 0.45 : 1,
                transition: 'all 0.15s',
                '&:hover': locked ? {} : { borderColor: s.color, bgcolor: s.bg },
            }}
        >
            <Typography sx={{
                fontSize: '10.5px', fontWeight: selected ? 700 : 600,
                color: selected ? s.color : '#6B7280', whiteSpace: 'nowrap',
            }}>
                {value}
            </Typography>
        </Box>
    );
});

const TimeCell = memo(function TimeCell({
    id, value, mark, onChange, icon, activeColor, mode, sameTimeEnabled,
}) {
    const needs = mark === 'Present' || mark === 'Late';
    if (!needs) {
        return (
            <Typography sx={{
                fontSize: '11px', fontStyle: 'italic', fontWeight: 500,
                color: mark === 'Absent' ? '#DC2626' : '#6246E0',
            }}>
                {mark === 'On Leave' ? 'On Leave' : '—'}
            </Typography>
        );
    }
    const disabled = sameTimeEnabled;
    const isEmpty = !value;
    return (
        <Tooltip
            title={disabled ? 'Controlled by "Same time for all" — disable toggle to edit individually' : ''}
            placement="top" arrow disableHoverListener={!disabled}
        >
            <span>
                <TextField
                    type="time"
                    size="small"
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(id, e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    {icon}
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{
                        width: 125,
                        '& .MuiOutlinedInput-root': {
                            fontSize: '12px', fontWeight: 600, height: 32, bgcolor: '#fff',
                            '& fieldset': {
                                borderColor: isEmpty && mode === 'in' ? '#FCA5A5' : '#E5E7EB',
                                borderWidth: 1,
                            },
                            '&:hover fieldset': { borderColor: activeColor },
                            '&.Mui-focused fieldset': { borderColor: activeColor, borderWidth: 1.5 },
                            '&.Mui-disabled': {
                                bgcolor: '#F9FAFB',
                                '& input': { color: '#6B7280', WebkitTextFillColor: '#6B7280' },
                            },
                        },
                    }}
                />
            </span>
        </Tooltip>
    );
});

// Column tracks for the Check In / Check Out table: spare width is shared out across the columns
// (instead of piling up after Staff Member), and columns never get narrower than their content.
const STAFF_GRID = '40px minmax(190px, 1.3fr) minmax(140px, 1fr) minmax(290px, 1.7fr) minmax(130px, 0.9fr) minmax(130px, 0.9fr) minmax(100px, 0.7fr) 56px';

const COL_STAFF = {
    serial:  40,
    member:  { flex: '1 1 180px', minWidth: 180 },
    role:    140,
    status:  290,
    checkIn: 130,
    checkOut: 130,
    work:    100,
    notes:   56,
};
const COL_BREAK = {
    serial:  46,
    member:  { flex: '0 0 220px', width: 220 },
    breaks:  { flex: '1 1 380px', minWidth: 380 },
    total:   120,
    count:   120,
};

const StaffRow = memo(function StaffRow({
    index, style,
    items, marks, ins, outs, notes, sameTimeEnabled,
    leaveMap, sourceMap, breaksMap,
    onMarkChange, onCheckInChange, onCheckOutChange, onOpenNotes,
}) {
    const staff = items[index];
    if (!staff) return null;

    const mark = marks[staff.id] || '';
    const inT  = ins[staff.id]  || '';
    const outT = outs[staff.id] || '';
    const note = notes[staff.id] || '';
    const work = computeWorkingHours(inT, outT);
    const breakMinForRow = work ? computeTotalBreakMinutes(breaksMap?.[staff.id] || []) : 0;
    const netWorkMin = work ? Math.max(0, work.totalMinutes - breakMinForRow) : 0;
    const roleConf = ROLE_CONFIG[staff.designation] || { color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' };
    const avColor = avatarColorFor(staff.name || '');
    const needsTime = mark === 'Present' || mark === 'Late';

    const leaveInfo = leaveMap?.[staff.id];
    const isLeaveLocked = !!leaveInfo?.isOnApprovedLeave;
    const isHalfDayLeave = isLeaveLocked && !!leaveInfo?.approvedLeaveIsHalfDay;

    const src = sourceMap?.[staff.id] || {};

    const checkInIcon = <AccessTimeIcon sx={{ fontSize: 14, color: PRIMARY }} />;
    const checkOutIcon = <LogoutIcon sx={{ fontSize: 14, color: '#1D4ED8' }} />;

    const renderSourceBadge = (source) => {
        if (!source) return null;
        const isBio = String(source).toLowerCase() === 'biometric';
        return (
            <Chip
                size="small"
                label={isBio ? 'Bio' : 'Manual'}
                sx={{
                    height: 14, fontSize: '8.5px', fontWeight: 700,
                    bgcolor: isBio ? '#EEF2FF' : '#F9FAFB',
                    color:   isBio ? '#4338CA' : '#6B7280',
                    border:  `1px solid ${isBio ? '#C7D2FE' : '#E5E7EB'}`,
                    mt: 0.3,
                    '& .MuiChip-label': { px: 0.6 },
                }}
            />
        );
    };

    return (
        <Box
            style={style}
            sx={{
                display: 'grid', gridTemplateColumns: STAFF_GRID, alignItems: 'center',
                px: 2, columnGap: 1.5,
                borderBottom: '1px solid #F3F4F6',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: PRIMARY_LIGHT },
            }}
        >
            <Box sx={{ width: COL_STAFF.serial, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500 }}>{index + 1}</Typography>
            </Box>
            <Box sx={{ ...COL_STAFF.member, display: 'flex', alignItems: 'center', gap: 1.1 }}>
                <Avatar src={staff.filePath || undefined}
                    sx={{
                        width: 32, height: 32,
                        bgcolor: `${avColor}15`,
                        color: avColor,
                        fontSize: '11px', fontWeight: 700,
                        border: `1px solid ${avColor}33`,
                        flexShrink: 0,
                    }}>
                    {staff.avatar}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827' }} noWrap>{staff.name}</Typography>
                        {isLeaveLocked && (
                            <Chip
                                size="small"
                                icon={<EventBusyIcon sx={{ fontSize: '11px !important' }} />}
                                label={isHalfDayLeave ? 'Half-Day Leave' : 'Approved Leave'}
                                sx={{
                                    height: 18, fontSize: '9.5px', fontWeight: 700,
                                    bgcolor: '#F3F0FE', color: '#1D4ED8',
                                    border: '1px solid #C9BEFB',
                                    '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                                    '& .MuiChip-label': { px: 0.7 },
                                }}
                            />
                        )}
                    </Box>
                    <Typography sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 500 }}>{staff.employeeCode}</Typography>
                </Box>
            </Box>
            <Box sx={{ width: COL_STAFF.role, flexShrink: 0 }}>
                <Chip label={staff.designation || '—'} size="small"
                    sx={{
                        bgcolor: roleConf.bg, color: roleConf.color,
                        border: `1px solid ${roleConf.border}`,
                        fontWeight: 600, fontSize: '10px', height: 22,
                    }} />
            </Box>
            <Box sx={{ width: COL_STAFF.status, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                    {STATUS_OPTIONS.map(opt => (
                        <StatusPill
                            key={opt}
                            value={opt}
                            selected={mark === opt}
                            locked={isLeaveLocked}
                            onClick={() => onMarkChange(staff.id, opt)}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ width: COL_STAFF.checkIn, flexShrink: 0 }}>
                <TimeCell
                    id={staff.id} value={inT} mark={mark} onChange={onCheckInChange}
                    icon={checkInIcon} activeColor={PRIMARY} mode="in" sameTimeEnabled={sameTimeEnabled}
                />
                {inT && renderSourceBadge(src.login)}
            </Box>
            <Box sx={{ width: COL_STAFF.checkOut, flexShrink: 0 }}>
                <TimeCell
                    id={staff.id} value={outT} mark={mark} onChange={onCheckOutChange}
                    icon={checkOutIcon} activeColor="#1D4ED8" mode="out" sameTimeEnabled={sameTimeEnabled}
                />
                {outT && renderSourceBadge(src.logout)}
            </Box>
            <Box sx={{ width: COL_STAFF.work, flexShrink: 0 }}>
                {needsTime && work ? (
                    <Tooltip
                        arrow placement="top"
                        title={
                            <Box sx={{ fontSize: '11px', lineHeight: 1.6 }}>
                                <div>Gross: <strong>{formatMinutes(work.totalMinutes)}</strong></div>
                                <div>Breaks: <strong>{formatMinutes(breakMinForRow)}</strong></div>
                                <div>Net: <strong>{formatMinutes(netWorkMin)}</strong></div>
                            </Box>
                        }
                    >
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Chip size="small" label={formatMinutes(netWorkMin)}
                                sx={{
                                    bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK,
                                    border: `1px solid ${PRIMARY_BORDER}`,
                                    fontWeight: 700, fontSize: '11px', height: 22,
                                }} />
                            {breakMinForRow > 0 && (
                                <Typography sx={{
                                    fontSize: '9.5px', fontWeight: 600, color: '#0EA5E9',
                                    fontFamily: 'monospace', mt: 0.3,
                                }}>
                                    − {formatMinutes(breakMinForRow)} brk
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                ) : (
                    <Typography sx={{ fontSize: '11px', color: '#D1D5DB' }}>—</Typography>
                )}
            </Box>
            <Box sx={{ width: COL_STAFF.notes, flexShrink: 0, textAlign: 'center' }}>
                <Tooltip title={note ? note : 'Add note'} placement="top" arrow>
                    <IconButton size="small"
                        onClick={(e) => onOpenNotes(e, staff.id)}
                        sx={{
                            color: note ? PRIMARY_DARK : '#9CA3AF',
                            bgcolor: note ? PRIMARY_LIGHT : 'transparent',
                            border: note ? `1px solid ${PRIMARY_BORDER}` : '1px solid transparent',
                            '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK, border: `1px solid ${PRIMARY_BORDER}` },
                        }}
                    >
                        <StickyNote2Icon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
});

const estimateBreakRowHeight = (mark, breaksCount) => {
    const needsTime = mark === 'Present' || mark === 'Late';
    if (!needsTime) return 60;
    const base = 60;
    const perBreak = 40;
    const addBtnRow = 40;
    return base + (breaksCount * perBreak) + addBtnRow;
};

const BreakRow = memo(function BreakRow({
    index, style,
    items, marks, ins, outs, breaksMap,
    onAddBreak, onUpdateBreak, onDeleteBreak, onAddPreset,
}) {
    const staff = items[index];
    if (!staff) return null;

    const mark = marks[staff.id] || '';
    const inT  = ins[staff.id]  || '';
    const outT = outs[staff.id] || '';
    const breaks = breaksMap[staff.id] || [];
    const totalBreakMin = computeTotalBreakMinutes(breaks);
    const avColor = avatarColorFor(staff.name || '');
    const needsTime = mark === 'Present' || mark === 'Late';

    /* eslint-disable no-unused-vars */
    const work = computeWorkingHours(inT, outT);
    const netWorkMin = work ? Math.max(0, work.totalMinutes - totalBreakMin) : 0;
    /* eslint-enable no-unused-vars */

    return (
        <Box
            style={style}
            sx={{
                display: 'flex', alignItems: 'flex-start',
                px: 2, py: 1.2, gap: 1.5,
                borderBottom: '1px solid #F3F4F6',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: PRIMARY_LIGHT },
            }}
        >
            <Box sx={{ width: COL_BREAK.serial, flexShrink: 0, pt: 0.4 }}>
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500 }}>{index + 1}</Typography>
            </Box>

            <Box sx={{ ...COL_BREAK.member, display: 'flex', alignItems: 'center', gap: 1.1, pt: 0.2 }}>
                <Avatar src={staff.filePath || undefined}
                    sx={{
                        width: 32, height: 32,
                        bgcolor: `${avColor}15`,
                        color: avColor,
                        fontSize: '11px', fontWeight: 700,
                        border: `1px solid ${avColor}33`,
                        flexShrink: 0,
                    }}>
                    {staff.avatar}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827' }} noWrap>{staff.name}</Typography>
                    <Typography sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 500 }}>{staff.employeeCode}</Typography>
                </Box>
            </Box>

            <Box sx={{ ...COL_BREAK.breaks }}>
                {!needsTime ? (
                    <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: '#9CA3AF' }}>
                        Breaks not applicable
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                        {breaks.length === 0 ? (
                            <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic', mb: 0.3 }}>
                                No breaks recorded yet
                            </Typography>
                        ) : breaks.map((br, i) => {
                            const dur = computeBreakDuration(br.out, br.in);
                            const isValid = dur > 0;
                            return (
                                <Box
                                    key={br.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.6,
                                        px: 0.8, py: 0.5,
                                        bgcolor: isValid ? '#E0F2FE' : '#F9FAFB',
                                        border: `1px solid ${isValid ? '#BAE6FD' : '#E5E7EB'}`,
                                        borderRadius: '7px',
                                    }}
                                >
                                    <Box sx={{
                                        width: 22, height: 22, borderRadius: '7px',
                                        bgcolor: isValid ? '#FFF' : '#F3F4F6',
                                        border: `1px solid ${isValid ? '#BAE6FD' : '#E5E7EB'}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <LocalCafeIcon sx={{ fontSize: 12, color: isValid ? '#0EA5E9' : '#9CA3AF' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: '#6B7280', minWidth: 42 }}>
                                        Break {i + 1}
                                    </Typography>
                                    <TextField
                                        type="time" size="small"
                                        value={br.out || ''}
                                        onChange={(e) => onUpdateBreak(staff.id, br.id, 'out', e.target.value)}
                                        sx={{
                                            width: 110,
                                            '& .MuiOutlinedInput-root': {
                                                fontSize: '11.5px', fontWeight: 600, height: 28, bgcolor: '#fff',
                                                '& fieldset': { borderColor: '#E5E7EB' },
                                                '&:hover fieldset': { borderColor: '#0EA5E9' },
                                                '&.Mui-focused fieldset': { borderColor: '#0EA5E9', borderWidth: 1.5 },
                                            },
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF' }}>→</Typography>
                                    <TextField
                                        type="time" size="small"
                                        value={br.in || ''}
                                        onChange={(e) => onUpdateBreak(staff.id, br.id, 'in', e.target.value)}
                                        sx={{
                                            width: 110,
                                            '& .MuiOutlinedInput-root': {
                                                fontSize: '11.5px', fontWeight: 600, height: 28, bgcolor: '#fff',
                                                '& fieldset': { borderColor: '#E5E7EB' },
                                                '&:hover fieldset': { borderColor: PRIMARY },
                                                '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 },
                                            },
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label={isValid ? formatMinutes(dur) : '—'}
                                        sx={{
                                            height: 20, minWidth: 42, fontSize: '10px', fontWeight: 700,
                                            bgcolor: isValid ? '#FEF3C7' : '#F3F4F6',
                                            color: isValid ? '#92400E' : '#9CA3AF',
                                            border: `1px solid ${isValid ? '#BAE6FD' : '#E5E7EB'}`,
                                        }}
                                    />
                                    <Tooltip arrow title="Delete this break">
                                        <IconButton
                                            size="small"
                                            aria-label={`Delete break ${i + 1}`}
                                            onClick={() => onDeleteBreak(staff.id, br.id)}
                                            sx={{ ml: 'auto', width: 26, height: 26, color: '#9CA3AF', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}
                                        >
                                            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            );
                        })}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mt: 0.2 }}>
                            <Button
                                size="small"
                                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onAddBreak(staff.id)}
                                sx={{
                                    textTransform: 'none', fontSize: '11px', fontWeight: 700,
                                    height: 26, borderRadius: '7px', px: 1,
                                    color: PRIMARY_DARK,
                                    border: `1px dashed ${PRIMARY_BORDER}`,
                                    bgcolor: '#fff',
                                    '&:hover': { bgcolor: PRIMARY_LIGHT, borderStyle: 'solid' },
                                }}
                            >
                                Add Break
                            </Button>
                            {BREAK_PRESETS.map(p => {
                                const PIcon = p.icon;
                                return (
                                    <Tooltip key={p.label} arrow title={`Quick add: ${p.label} (${p.out} – ${p.in})`}>
                                        <Button
                                            size="small"
                                            startIcon={<PIcon sx={{ fontSize: 13 }} />}
                                            onClick={() => onAddPreset(staff.id, p)}
                                            sx={{
                                                textTransform: 'none', fontSize: '10.5px', fontWeight: 600,
                                                height: 26, borderRadius: '7px', px: 0.8,
                                                color: p.accent, bgcolor: p.accentBg,
                                                border: `1px solid ${p.accentBorder}`,
                                                '&:hover': { bgcolor: p.accentBg, borderColor: p.accent, filter: 'brightness(0.97)' },
                                            }}
                                        >
                                            {p.label}
                                        </Button>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            <Box sx={{ width: COL_BREAK.total, flexShrink: 0, pt: 0.4 }}>
                {needsTime ? (
                    <Chip
                        size="small"
                        icon={<LocalCafeIcon sx={{ fontSize: '12px !important' }} />}
                        label={formatMinutes(totalBreakMin)}
                        sx={{
                            bgcolor: totalBreakMin > 0 ? '#E0F2FE' : '#F3F4F6',
                            color: totalBreakMin > 0 ? '#92400E' : '#9CA3AF',
                            border: `1px solid ${totalBreakMin > 0 ? '#BAE6FD' : '#E5E7EB'}`,
                            fontWeight: 700, fontSize: '11px', height: 22,
                            '& .MuiChip-icon': { color: 'inherit', ml: '6px' },
                        }}
                    />
                ) : (
                    <Typography sx={{ fontSize: '11px', color: '#D1D5DB' }}>—</Typography>
                )}
            </Box>

            <Box sx={{ width: COL_BREAK.count, flexShrink: 0, pt: 0.4 }}>
                {needsTime ? (
                    <Chip
                        size="small"
                        icon={<TimerOutlinedIcon sx={{ fontSize: '12px !important' }} />}
                        label={`${breaks.length} ${breaks.length === 1 ? 'break' : 'breaks'}`}
                        sx={{
                            bgcolor: breaks.length > 0 ? PRIMARY_LIGHT : '#F3F4F6',
                            color: breaks.length > 0 ? PRIMARY_DARK : '#9CA3AF',
                            border: `1px solid ${breaks.length > 0 ? PRIMARY_BORDER : '#E5E7EB'}`,
                            fontWeight: 700, fontSize: '11px', height: 22,
                            '& .MuiChip-icon': { color: 'inherit', ml: '6px' },
                        }}
                    />
                ) : (
                    <Typography sx={{ fontSize: '11px', color: '#D1D5DB' }}>—</Typography>
                )}
            </Box>
        </Box>
    );
});

export default function AddStaffAttendancePage() {
    const navigate = useNavigate();
    const user = useSelector(state => state.auth);
    const financialYear = useFinancialYear();
    const currentUserRoll = user?.rollNumber || '';

    const [attendanceDate, setAttendanceDate] = useState(today);
    const dateInputRef = useRef(null);
    const [designationFilter, setDesignationFilter] = useState('All');
    const [searchText, setSearchText] = useState('');

    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isAttendanceAdded, setIsAttendanceAdded] = useState(false);

    const [activeTab, setActiveTab] = useState(0);

    const [attendanceMarks, setAttendanceMarks] = useState({});
    const [checkInTimes, setCheckInTimes]       = useState({});
    const [checkOutTimes, setCheckOutTimes]     = useState({});
    const [rowNotes, setRowNotes]               = useState({});
    const [breaksMap, setBreaksMap]             = useState({});

    const [leaveMap, setLeaveMap]   = useState({});
    const [sourceMap, setSourceMap] = useState({});

    // The "Same time for all" bar was removed, so rows are never locked by it.
    const [sameTimeEnabled] = useState(false);

    const [notesAnchor, setNotesAnchor] = useState(null);
    const [notesTargetId, setNotesTargetId] = useState(null);

    const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);

    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(false);
    const [color, setColor] = useState(false);
    const [message, setMessage] = useState('');
    const showSnack = (msg, success) => {
        setMessage(msg); setOpen(true); setColor(success); setStatus(success);
    };

    const trimSec = (t) => (t && t.length >= 5 ? t.slice(0, 5) : (t || ''));

    const mapAttendanceRow = (emp, record) => {
        const punches = Array.isArray(record?.punches) ? record.punches : [];
        const firstPunch = punches.length > 0 ? punches[0] : null;
        const lastPunch  = punches.length > 0 ? punches[punches.length - 1] : null;

        const existingBreaks = Array.isArray(record?.breaks)
            ? record.breaks.map(b => ({
                id: makeBreakId(),
                out: trimSec(b.breakOutTime),
                in:  trimSec(b.breakInTime),
                breakNo: Number.isInteger(b.breakNo) ? b.breakNo : undefined,
                outSource: b.breakOutSource || '',
                inSource:  b.breakInSource  || '',
            })).filter(b => b.out || b.in)
            : [];

        const name = emp.name || record?.employeeName || 'Unknown';
        const leaveRow = Array.isArray(record?.leaves) ? record.leaves.find(l => l?.isOnApprovedLeave) : null;

        return {
            id: emp.employeeCode,
            employeeCode: emp.employeeCode,
            biometricEmployeeId: emp.biometricEmployeeId || record?.biometricEmployeeId || null,
            name,
            designation: emp.designation || '',
            department: emp.department || '',
            avatar: name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase(),
            filePath: emp.filePath || record?.profilePath || '',
            existingStatus: record?.status || '',
            existingCheckIn: trimSec(firstPunch?.loginTime),
            existingCheckOut: trimSec(lastPunch?.logoutTime),
            existingNotes: '',
            existingBreaks,
            leave: leaveRow
                ? { isOnApprovedLeave: true, approvedLeaveIsHalfDay: !!leaveRow.approvedLeaveIsHalfDay }
                : null,
            source: {
                login:  firstPunch?.loginSource  || '',
                logout: lastPunch?.logoutSource  || '',
            },
        };
    };

    const initRowState = (staff) => {
        const marks = {}, ins = {}, outs = {}, notes = {}, brks = {}, leaves = {}, srcs = {};
        staff.forEach(s => {
            const fromServer = STATUS_API_TO_UI[String(s.existingStatus).toLowerCase()];
            marks[s.id] = s.leave ? 'On Leave' : (fromServer || '');
            ins[s.id]   = s.existingCheckIn;
            outs[s.id]  = s.existingCheckOut;
            notes[s.id] = s.existingNotes;
            brks[s.id]  = s.existingBreaks || [];
            if (s.leave)  leaves[s.id] = s.leave;
            if (s.source) srcs[s.id]   = s.source;
        });
        return { marks, ins, outs, notes, brks, leaves, srcs };
    };

    const fetchAttendance = async () => {
        const apiDate = toApiDate(attendanceDate);
        if (!apiDate || !financialYear) return;

        setLoading(true);
        try {
            const [rosterRes, recordRes] = await Promise.all([
                http.get(GetAttendanceEmployeeBefore, { params: { financialYear } }),
                http.get(GetEmployeeAttendance, { params: { fromDate: apiDate, toDate: apiDate } })
                    .catch(err => (err?.response?.status === 404 ? { data: { data: [] } } : Promise.reject(err))),
            ]);

            if (rosterRes?.data?.error) {
                showSnack(rosterRes.data.message || 'Failed to load the employee list', false);
                return;
            }

            const roster = Array.isArray(rosterRes?.data?.details) ? rosterRes.data.details : [];
            const records = Array.isArray(recordRes?.data?.data) ? recordRes.data.data : [];
            const byCode = new Map(records.map(r => [r.employeeCode, r]));

            const staff = roster
                .filter(e => e.employeeCode)
                .map(e => mapAttendanceRow(e, byCode.get(e.employeeCode)));

            setStaffList(staff);
            setIsAttendanceAdded(staff.some(s => String(s.existingStatus).trim().length > 0));

            const { marks, ins, outs, notes, brks, leaves, srcs } = initRowState(staff);
            setAttendanceMarks(marks);
            setCheckInTimes(ins);
            setCheckOutTimes(outs);
            setRowNotes(notes);
            setBreaksMap(brks);
            setLeaveMap(leaves);
            setSourceMap(srcs);
        } catch (err) {
            console.error('Attendance load failed:', err);
            showSnack(err?.response?.data?.message || 'Failed to load attendance', false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAttendance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendanceDate, financialYear]);
    const needsTime = (id) => {
        const s = attendanceMarks[id];
        return s === 'Present' || s === 'Late';
    };

    const designations = useMemo(
        () => ['All', ...Array.from(new Set(staffList.map(s => s.designation).filter(Boolean))).sort()],
        [staffList]
    );

    const filteredStaff = useMemo(() =>
        staffList.filter(s => {
            const matchDesignation = designationFilter === 'All' || s.designation === designationFilter;
            const q = searchText.trim().toLowerCase();
            const matchSearch = !q ||
                s.name.toLowerCase().includes(q) ||
                String(s.employeeCode).toLowerCase().includes(q);
            return matchDesignation && matchSearch;
        }),
        [staffList, designationFilter, searchText]
    );

    const counts = useMemo(() => ({
        present: filteredStaff.filter(s => attendanceMarks[s.id] === 'Present').length,
        absent:  filteredStaff.filter(s => attendanceMarks[s.id] === 'Absent').length,
        late:    filteredStaff.filter(s => attendanceMarks[s.id] === 'Late').length,
        onLeave: filteredStaff.filter(s => attendanceMarks[s.id] === 'On Leave').length,
    }), [filteredStaff, attendanceMarks]);

    const handleMarkChange = useCallback((id, newMark) => {
        setAttendanceMarks(prev => (prev[id] === newMark ? prev : { ...prev, [id]: newMark }));
        if (newMark === 'Absent' || newMark === 'On Leave') {
            setCheckInTimes(prev => (prev[id] ? { ...prev, [id]: '' } : prev));
            setCheckOutTimes(prev => (prev[id] ? { ...prev, [id]: '' } : prev));
        }
    }, []);

    const handleCheckInChange = useCallback((id, value) => {
        setCheckInTimes(prev => ({ ...prev, [id]: value }));
        setAttendanceMarks(prev => {
            const cur = prev[id];
            if (cur === 'Present' && isLateTime(value)) return { ...prev, [id]: 'Late' };
            if (cur === 'Late' && value && !isLateTime(value)) return { ...prev, [id]: 'Present' };
            return prev;
        });
    }, []);

    const checkInTimesRef = useRef(checkInTimes);
    useEffect(() => { checkInTimesRef.current = checkInTimes; }, [checkInTimes]);

    const handleCheckOutChange = useCallback((id, value) => {
        const checkIn = checkInTimesRef.current[id];
        if (checkIn && parseHHmm(value) !== null && parseHHmm(value) <= parseHHmm(checkIn)) {
            showSnack('Check-out must be after check-in', false);
            return;
        }
        setCheckOutTimes(prev => ({ ...prev, [id]: value }));
    }, []);

    const handleOpenNotes = useCallback((e, id) => {
        setNotesAnchor(e.currentTarget);
        setNotesTargetId(id);
    }, []);

    const handleFillCurrentTime = () => {
        const now = getCurrentTime();
        setCheckInTimes(prev => {
            const updated = { ...prev };
            filteredStaff.forEach(s => {
                if (needsTime(s.id) && !updated[s.id]) updated[s.id] = now;
            });
            return updated;
        });
    };

    const handleFillDefaultCheckOut = () => {
        setCheckOutTimes(prev => {
            const updated = { ...prev };
            filteredStaff.forEach(s => {
                if (needsTime(s.id) && !updated[s.id]) updated[s.id] = DEFAULT_CHECK_OUT;
            });
            return updated;
        });
    };

    const handleBulkStatus = (newMark) => {
        setBulkMenuAnchor(null);
        setAttendanceMarks(prev => {
            const updated = { ...prev };
            filteredStaff.forEach(s => { updated[s.id] = newMark; });
            return updated;
        });
        if (newMark === 'Absent' || newMark === 'On Leave') {
            setCheckInTimes(prev => {
                const updated = { ...prev };
                filteredStaff.forEach(s => { updated[s.id] = ''; });
                return updated;
            });
            setCheckOutTimes(prev => {
                const updated = { ...prev };
                filteredStaff.forEach(s => { updated[s.id] = ''; });
                return updated;
            });
        }
    };

    const closeNotesPopover = () => { setNotesAnchor(null); setNotesTargetId(null); };

    const handleAddBreak = useCallback((staffId) => {
        setBreaksMap(prev => ({
            ...prev,
            [staffId]: [...(prev[staffId] || []), { id: makeBreakId(), out: '', in: '' }],
        }));
    }, []);

    const handleAddPreset = useCallback((staffId, preset) => {
        setBreaksMap(prev => ({
            ...prev,
            [staffId]: [...(prev[staffId] || []), { id: makeBreakId(), out: preset.out, in: preset.in }],
        }));
    }, []);

    const handleUpdateBreak = useCallback((staffId, breakId, field, value) => {
        setBreaksMap(prev => {
            const list = prev[staffId] || [];
            return {
                ...prev,
                [staffId]: list.map(br => (br.id === breakId ? { ...br, [field]: value } : br)),
            };
        });
    }, []);

    const handleDeleteBreak = useCallback((staffId, breakId) => {
        setBreaksMap(prev => {
            const list = (prev[staffId] || []).filter(br => br.id !== breakId);
            return { ...prev, [staffId]: list };
        });
    }, []);

    const handleSaveAttendance = async () => {
        const toApiTime = (hhmm) => (hhmm && hhmm.length === 5 ? `${hhmm}:00` : (hhmm || ''));

        let skippedIncomplete = 0;
        const items = filteredStaff.map(s => {
            const statusUI = attendanceMarks[s.id];
            if (!statusUI) return null;
            const apiStatus = STATUS_UI_TO_API[statusUI] || 'present';
            const hasTime = statusUI === 'Present' || statusUI === 'Late';
            if (hasTime && !checkInTimes[s.id]) {
                skippedIncomplete += 1;
                return null;
            }
            const checkIn  = hasTime ? checkInTimes[s.id]  : '';
            const checkOut = hasTime ? (checkOutTimes[s.id] || '') : '';

            const breaks = (breaksMap[s.id] || [])
                .filter(br => computeBreakDuration(br.out, br.in) > 0)
                .map((br, i) => ({
                    BreakNo: Number.isInteger(br.breakNo) ? br.breakNo : i + 1,
                    BreakOutTime: toApiTime(br.out),
                    BreakInTime:  toApiTime(br.in),
                }));

            const item = {
                EmployeeCode: s.employeeCode,
                BiometricEmployeeId: s.biometricEmployeeId ?? null,
                Status: apiStatus,
                Breaks: breaks,
            };
            if (hasTime && checkIn)  item.LoginTime  = toApiTime(checkIn);
            if (hasTime && checkOut) item.LogoutTime = toApiTime(checkOut);
            return item;
        }).filter(Boolean);

        if (items.length === 0) {
            showSnack(
                skippedIncomplete > 0
                    ? `Add a check-in time for the ${skippedIncomplete} Present/Late member(s) to save.`
                    : 'Mark at least one staff member to save attendance.',
                false
            );
            return;
        }

        const allNotes = filteredStaff
            .map(s => (rowNotes[s.id] || '').trim())
            .filter(n => n.length > 0);
        const reason = allNotes.length > 0 ? allNotes.join(' · ') : '';

        if (!financialYear) {
            showSnack('Set the financial year first — attendance is saved against it.', false);
            return;
        }

        const body = {
            Date: toApiDate(attendanceDate),
            FinancialYear: financialYear,
            Reason: reason,
            Items: items,
        };

        setSaving(true);
        try {
            const res = await http.post(PostEmployeeManualAttendance, body);
            if (res?.data && res.data.error) {
                showSnack(res.data.message || 'Failed to save attendance', false);
                return;
            }
            const baseMsg = `Attendance saved for ${items.length} member${items.length === 1 ? '' : 's'}`;
            const tail = skippedIncomplete > 0
                ? ` · ${skippedIncomplete} skipped (missing check-in)`
                : '';
            showSnack(baseMsg + tail, true);
            await fetchAttendance();
        } catch (error) {
            showSnack(error?.response?.data?.message || 'Failed to save attendance', false);
        } finally {
            setSaving(false);
        }
    };

    // Page controls live at the right end of the breadcrumb row. The "Manual Entry Mode" notice
    // is a hover message on its icon chip instead of a banner.
    const setCrumbActions = useCrumbActionsSetter();
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'nowrap' }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.6, height: 42, width: 'fit-content', flexShrink: 0,
                        bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`, borderRadius: '7px', px: 1,
                    }}>
                    <EventIcon sx={{ fontSize: 16, color: PRIMARY, cursor: 'pointer' }}
                        onClick={() => { try { dateInputRef.current?.showPicker(); } catch { dateInputRef.current?.focus(); } }} />
                    <TextField
                        type="date" size="small" value={attendanceDate}
                        onChange={(e) => setAttendanceDate(e.target.value)}
                        variant="standard"
                        slotProps={{
                            input: { disableUnderline: true, style: { fontSize: '12px', fontWeight: 600, color: PRIMARY_DARK } },
                            htmlInput: {
                                ref: dateInputRef,
                                // the leading icon is the only calendar icon; clicking the field itself also opens the picker
                                onClick: (e) => { try { e.currentTarget.showPicker(); } catch { /* unsupported */ } },
                                style: { padding: 0, cursor: 'pointer' },
                            },
                        }}
                        sx={{ width: 92, '& input::-webkit-calendar-picker-indicator': { display: 'none' } }}
                    />
                    </Box>
                <TextField
                    size="small"
                    placeholder="Search staff name or ID..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /></InputAdornment>) } }}
                    sx={{ width: { xs: 160, md: 240 }, flexShrink: 1, '& .MuiOutlinedInput-root': { height: 42, fontSize: '13px', borderRadius: '50px', bgcolor: '#fff', '& fieldset': { borderColor: '#E5E7EB' } } }}
                />
                <Box sx={{ width: 150, flexShrink: 0 }}>
                    <SearchSelect
                        value={designationFilter} onChange={(e) => setDesignationFilter(e.target.value)} searchPlaceholder="Search designations…"
                        sx={{ bgcolor: '#fff', borderRadius: '50px', '& .MuiOutlinedInput-root': { height: 42, fontSize: '13px', borderRadius: '50px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' } }}
                        options={designations.map((d) => ({ value: d, label: d === 'All' ? 'All designations' : d }))}
                    />
                </Box>
                <Button
                    size="small" variant="outlined"
                    startIcon={<HistoryIcon sx={{ fontSize: 17 }} />}
                    onClick={() => navigate(`/dashboard/attendance-history?date=${attendanceDate}`)}
                    sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, height: 42, borderRadius: '50px', borderColor: '#D1D5DB', color: '#374151', bgcolor: '#fff', px: 1.4, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' } }}
                >
                    Edit History
                </Button>
                <Button
                    size="small" variant="outlined"
                    startIcon={<DoneAllIcon />}
                    onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
                    sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, height: 42, borderRadius: '50px', borderColor: '#D1D5DB', color: '#374151', bgcolor: '#fff', px: 1.4, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' } }}
                >
                    Bulk Actions
                </Button>
            </Box>,
        );
        return () => setCrumbActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, attendanceDate, searchText, designationFilter, designations, currentUserRoll]);

    return (
        <Box sx={{ p: 2 }}>
            <SnackBar open={open} color={color} setOpen={setOpen} status={status} message={message} />

            <Box sx={{
                mb: 1.5, px: 1.5, py: 1, borderRadius: '7px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                display: 'flex', alignItems: 'center', gap: 1.2,
            }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '8px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FingerprintIcon sx={{ fontSize: 20, color: PRIMARY }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: PRIMARY_DARK }}>Manual Entry Mode · Biometric Fallback</Typography>
                    <Typography sx={{ fontSize: '12px', color: PRIMARY_DARK }}>
                        Use this screen <strong>only when the biometric device is offline or to correct existing punches</strong>.
                        All check-in / check-out and break records are tagged <strong>source: manual</strong> and
                        audit-logged against <strong>{currentUserRoll || 'your roll number'}</strong>.
                    </Typography>
                </Box>
            </Box>

            {isAttendanceAdded && (
                <Box sx={{
                    mb: 2, px: 1.5, py: 1, borderRadius: '7px',
                    bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                    display: 'flex', alignItems: 'center', gap: 1,
                }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: PRIMARY }} />
                    <Typography sx={{ fontSize: '12px', color: PRIMARY_DARK, fontWeight: 500 }}>
                        Attendance already marked on this date — edits will update the existing record.
                    </Typography>
                </Box>
            )}

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                    { label: 'Present',  count: counts.present, icon: CheckCircleIcon, tone: 'green' },
                    { label: 'Late',     count: counts.late,    icon: AccessTimeIcon,  tone: 'amber' },
                    { label: 'Absent',   count: counts.absent,  icon: ClearAllIcon,    tone: 'rose' },
                    { label: 'On Leave', count: counts.onLeave, icon: EventIcon,       tone: 'purple' },
                ].map((item) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={item.label}>
                        <AttStatCard label={item.label} value={item.count} icon={item.icon} tone={item.tone} />
                    </Grid>
                ))}
            </Grid>

                <Menu
                    anchorEl={bulkMenuAnchor}
                    open={Boolean(bulkMenuAnchor)}
                    onClose={() => setBulkMenuAnchor(null)}
                    slotProps={{ paper: { sx: { borderRadius: '7px', minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '1px solid #E5E7EB', mt: 0.5 } } }}
                >
                    {STATUS_OPTIONS.map(opt => (
                        <MenuItem key={opt} onClick={() => handleBulkStatus(opt)} sx={{ fontSize: '13px', fontWeight: 600 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_STYLE[opt].color, mr: 1.2 }} />
                            Mark all as {opt}
                        </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem onClick={handleFillCurrentTime} sx={{ fontSize: '13px', fontWeight: 600 }}>
                        <FlashOnIcon sx={{ fontSize: 16, color: PRIMARY, mr: 1 }} />
                        Fill current time (check-in)
                    </MenuItem>
                    <MenuItem onClick={handleFillDefaultCheckOut} sx={{ fontSize: '13px', fontWeight: 600 }}>
                        <LogoutIcon sx={{ fontSize: 16, color: '#1D4ED8', mr: 1 }} />
                        Fill default check-out (5:00 PM)
                    </MenuItem>
                </Menu>

            <Card sx={{ boxShadow: 'none', border: '1px solid #E5E7EB', borderRadius: '7px', bgcolor: '#fff', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #E5E7EB', px: 1.5, bgcolor: '#FAFBFD' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{
                            minHeight: 42,
                            '& .MuiTab-root': {
                                textTransform: 'none', fontSize: '12.5px', fontWeight: 600,
                                minHeight: 42, color: '#6B7280', px: 2, gap: 0.6,
                            },
                            '& .Mui-selected': { color: `${PRIMARY} !important`, fontWeight: 700 },
                            '& .MuiTabs-indicator': { bgcolor: PRIMARY, height: 2.5, borderRadius: '2px 2px 0 0' },
                        }}
                    >
                        <Tab
                            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                            iconPosition="start"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    Check In / Check Out
                                    <Chip
                                        label={counts.present + counts.late}
                                        size="small"
                                        sx={{
                                            height: 18, fontSize: '10px', fontWeight: 700,
                                            bgcolor: activeTab === 0 ? PRIMARY_LIGHT : '#F3F4F6',
                                            color: activeTab === 0 ? PRIMARY_DARK : '#6B7280',
                                            border: `1px solid ${activeTab === 0 ? PRIMARY_BORDER : '#E5E7EB'}`,
                                        }}
                                    />
                                </Box>
                            }
                        />
                        <Tab
                            icon={<LocalCafeIcon sx={{ fontSize: 16 }} />}
                            iconPosition="start"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    Break In / Break Out
                                    {(() => {
                                        const totalBreaks = filteredStaff.reduce((sum, s) => sum + ((breaksMap[s.id] || []).length), 0);
                                        return (
                                            <Chip
                                                label={totalBreaks}
                                                size="small"
                                                sx={{
                                                    height: 18, fontSize: '10px', fontWeight: 700,
                                                    bgcolor: activeTab === 1 ? '#E0F2FE' : '#F3F4F6',
                                                    color: activeTab === 1 ? '#92400E' : '#6B7280',
                                                    border: `1px solid ${activeTab === 1 ? '#BAE6FD' : '#E5E7EB'}`,
                                                }}
                                            />
                                        );
                                    })()}
                                </Box>
                            }
                        />
                    </Tabs>
                </Box>

                {loading ? (
                    <SkeletonRows count={5} />
                ) : activeTab === 0 ? (
                    <Box>
                      <Box>
                        <Box sx={{
                            display: 'grid', gridTemplateColumns: STAFF_GRID, alignItems: 'center',
                            px: 2, py: 1.4, columnGap: 1.5,
                            bgcolor: PRIMARY_LIGHT,
                            borderBottom: `1px solid ${PRIMARY_BORDER}`,
                            position: 'sticky', top: 0, zIndex: 2,
                            '& > *': {
                                fontSize: '10px', fontWeight: 700, color: PRIMARY_DARK,
                                letterSpacing: 0.6, textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                            },
                        }}>
                            <Box sx={{ width: COL_STAFF.serial, flexShrink: 0 }}>#</Box>
                            <Box sx={{ ...COL_STAFF.member }}>Staff Member</Box>
                            <Box sx={{ width: COL_STAFF.role, flexShrink: 0 }}>Role</Box>
                            <Box sx={{ width: COL_STAFF.status, flexShrink: 0 }}>Status</Box>
                            <Box sx={{ width: COL_STAFF.checkIn, flexShrink: 0 }}>Check-In</Box>
                            <Box sx={{ width: COL_STAFF.checkOut, flexShrink: 0 }}>Check-Out</Box>
                            <Box sx={{ width: COL_STAFF.work, flexShrink: 0 }}>Working Hrs</Box>
                            <Box sx={{ width: COL_STAFF.notes, flexShrink: 0, textAlign: 'center' }}>Notes</Box>
                        </Box>

                        {filteredStaff.length === 0 ? (
                            <Typography sx={{ fontSize: '13px', color: '#9CA3AF', py: 4, textAlign: 'center' }}>
                                {staffList.length === 0 ? 'No staff data available for this date' : 'No staff found'}
                            </Typography>
                        ) : (
                            <Box sx={{ height: 'min(56vh, 600px)', minHeight: 320 }}>
                                <List
                                    rowCount={filteredStaff.length}
                                    rowHeight={64}
                                    rowComponent={StaffRow}
                                    rowProps={{
                                        items: filteredStaff,
                                        marks: attendanceMarks,
                                        ins: checkInTimes,
                                        outs: checkOutTimes,
                                        notes: rowNotes,
                                        sameTimeEnabled,
                                        leaveMap,
                                        sourceMap,
                                        breaksMap,
                                        onMarkChange: handleMarkChange,
                                        onCheckInChange: handleCheckInChange,
                                        onCheckOutChange: handleCheckOutChange,
                                        onOpenNotes: handleOpenNotes,
                                    }}
                                    overscanCount={4}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </Box>
                        )}
                      </Box>
                    </Box>
                ) : (
                    <>
                        <Box>
                          <Box>
                        <Box sx={{
                            display: 'flex', alignItems: 'center',
                            px: 2, py: 1.4, gap: 1.5,
                            bgcolor: PRIMARY_LIGHT,
                            borderBottom: `1px solid ${PRIMARY_BORDER}`,
                            position: 'sticky', top: 0, zIndex: 2,
                            '& > *': {
                                fontSize: '10px', fontWeight: 700, color: PRIMARY_DARK,
                                letterSpacing: 0.6, textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                            },
                        }}>
                            <Box sx={{ width: COL_BREAK.serial, flexShrink: 0 }}>#</Box>
                            <Box sx={{ ...COL_BREAK.member }}>Staff Member</Box>
                            <Box sx={{ ...COL_BREAK.breaks }}>Breaks (Out → In)</Box>
                            <Box sx={{ width: COL_BREAK.total, flexShrink: 0 }}>Total Break</Box>
                            <Box sx={{ width: COL_BREAK.count, flexShrink: 0 }}>Breaks Count</Box>
                        </Box>

                        {filteredStaff.length === 0 ? (
                            <Typography sx={{ fontSize: '13px', color: '#9CA3AF', py: 4, textAlign: 'center' }}>
                                {staffList.length === 0 ? 'No staff data available for this date' : 'No staff found'}
                            </Typography>
                        ) : (
                            <Box sx={{ height: 'min(56vh, 600px)', minHeight: 320 }}>
                                <List
                                    rowCount={filteredStaff.length}
                                    rowHeight={(index) => {
                                        const s = filteredStaff[index];
                                        if (!s) return 60;
                                        const m = attendanceMarks[s.id] || 'Present';
                                        const brs = breaksMap[s.id] || [];
                                        return estimateBreakRowHeight(m, brs.length);
                                    }}
                                    rowComponent={BreakRow}
                                    rowProps={{
                                        items: filteredStaff,
                                        marks: attendanceMarks,
                                        ins: checkInTimes,
                                        outs: checkOutTimes,
                                        breaksMap,
                                        onAddBreak: handleAddBreak,
                                        onUpdateBreak: handleUpdateBreak,
                                        onDeleteBreak: handleDeleteBreak,
                                        onAddPreset: handleAddPreset,
                                    }}
                                    overscanCount={3}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </Box>
                        )}
                          </Box>
                        </Box>
                    </>
                )}

                {!loading && (
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        px: 2, py: 1.2, borderTop: '1px solid #E5E7EB', bgcolor: '#F9FAFB',
                        flexWrap: 'wrap', gap: 1,
                    }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                                <strong style={{ color: '#111827' }}>{filteredStaff.length}</strong> staff
                            </Typography>
                            <Divider orientation="vertical" flexItem />
                            {activeTab === 0 ? (
                                <Typography sx={{ fontSize: '11px', color: '#6B7280' }}>
                                    Present <strong style={{ color: STATUS_STYLE.Present.color }}>{counts.present}</strong>
                                    {' · '}Late <strong style={{ color: STATUS_STYLE.Late.color }}>{counts.late}</strong>
                                    {' · '}Absent <strong style={{ color: STATUS_STYLE.Absent.color }}>{counts.absent}</strong>
                                    {' · '}Leave <strong style={{ color: STATUS_STYLE['On Leave'].color }}>{counts.onLeave}</strong>
                                </Typography>
                            ) : (() => {
                                const totalBreaks = filteredStaff.reduce((sum, s) => sum + ((breaksMap[s.id] || []).length), 0);
                                const totalBreakMin = filteredStaff.reduce((sum, s) => sum + computeTotalBreakMinutes(breaksMap[s.id] || []), 0);
                                const staffWithBreaks = filteredStaff.filter(s => (breaksMap[s.id] || []).length > 0).length;
                                return (
                                    <Typography sx={{ fontSize: '11px', color: '#6B7280' }}>
                                        Total Breaks <strong style={{ color: '#92400E' }}>{totalBreaks}</strong>
                                        {' · '}Staff with Breaks <strong style={{ color: '#111827' }}>{staffWithBreaks}/{counts.present + counts.late}</strong>
                                        {' · '}Combined Break Time <strong style={{ color: '#92400E' }}>{formatMinutes(totalBreakMin)}</strong>
                                    </Typography>
                                );
                            })()}
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <SaveIcon />}
                            onClick={handleSaveAttendance}
                            disabled={staffList.length === 0 || saving || loading}
                            sx={{
                                textTransform: 'none', fontSize: '13px', fontWeight: 700,
                                bgcolor: isAttendanceAdded ? '#1D4ED8' : PRIMARY,
                                color: '#fff', height: 40,
                                borderRadius: '7px', px: 3,
                                boxShadow: `0 2px 6px ${isAttendanceAdded ? '#1D4ED8' : PRIMARY}33`,
                                '& .MuiButton-startIcon': { color: '#fff' },
                                '&:hover': {
                                    bgcolor: isAttendanceAdded ? '#1E40AF' : PRIMARY_DARK,
                                    boxShadow: `0 4px 12px ${isAttendanceAdded ? '#1D4ED8' : PRIMARY}55`,
                                },
                                '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8', boxShadow: 'none', '& .MuiButton-startIcon': { color: '#94A3B8' } },
                            }}
                        >
                            {saving ? 'Saving…' : (isAttendanceAdded ? 'Update Attendance' : 'Save Attendance')}
                        </Button>
                    </Box>
                )}
            </Card>

            <Popover
                open={Boolean(notesAnchor)}
                anchorEl={notesAnchor}
                onClose={closeNotesPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { borderRadius: '7px', border: '1px solid #E5E7EB', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', mt: 0.5 } } }}
            >
                <Paper sx={{ p: 1.5, width: 280 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                        <StickyNote2Icon sx={{ fontSize: 14, color: PRIMARY }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>
                            Add Note
                        </Typography>
                    </Box>
                    <TextField
                        multiline minRows={3} fullWidth size="small"
                        placeholder="Remarks for this day (optional)"
                        value={notesTargetId ? (rowNotes[notesTargetId] || '') : ''}
                        onChange={(e) => setRowNotes(prev => ({ ...prev, [notesTargetId]: e.target.value }))}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '12px', borderRadius: '7px',
                                '&.Mui-focused fieldset': { borderColor: PRIMARY },
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button size="small" variant="contained" onClick={closeNotesPopover}
                            sx={{
                                textTransform: 'none', fontSize: '12px', fontWeight: 700,
                                bgcolor: PRIMARY, borderRadius: '7px', px: 2,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                            }}>
                            Done
                        </Button>
                    </Box>
                </Paper>
            </Popover>
        </Box>
    );
}
